import { Controller } from "@hotwired/stimulus"

// Calendario de rango del campo "Rango de fechas" (V2::DateRangeFieldComponent)
// y del período de los historiales (V2::PeriodFieldComponent).
//
// El campo nace en el DOM como dos <input type="date"> nativos: eso es lo que
// viaja al servidor (`from` y `to`) y lo que funciona sin JS. Este controller
// enciende un botón que abre UN calendario de dos meses donde el rango se
// elige con dos clicks: el primero fija el inicio, el segundo el término. Los
// inputs siguen siendo la fuente de verdad — cada selección los reescribe —,
// así que el form no sabe que hubo un popover.
//
// Dos caras según el campo: en el de reportes el botón REEMPLAZA a los inputs
// (target `native`, que se esconde) y muestra el rango elegido (`display`);
// en el período los inputs quedan a la vista, cada uno con su hora al lado, y
// el botón es solo el icono del calendario. Esos dos targets son opcionales.
//
// El pack V2 no carga el JS de Bootstrap: el popover, el foco y el teclado son
// propios (misma decisión que `v2--modal` y `v2--filter-panel`).
export default class extends Controller {
  static targets = [
    "native", "from", "to",
    "picker", "trigger", "display", "pop", "title", "months", "hint"
  ]

  static values = {
    months: Array,
    abbrMonths: Array,
    weekdays: Array,
    placeholder: String,
    fromPrefix: String,
    toPrefix: String,
    pickStart: String,
    pickEnd: String
  }

  connect() {
    this.from = parseISO(this.fromTarget.value)
    this.to = parseISO(this.toTarget.value)
    this.focusDate = this.from || this.to || today()
    this.viewMonth = startOfMonth(this.focusDate)

    // `required` vive en los inputs para el camino sin JS. Escondidos, el
    // navegador no puede enfocarlos para pedir el dato y bloquearía el submit
    // sin decir dónde: la validación queda del lado del servidor, que ya la
    // hace (el form de reportes re-renderiza con errores).
    this.fromTarget.required = false
    this.toTarget.required = false

    if (this.hasNativeTarget) this.nativeTarget.hidden = true
    this.pickerTarget.hidden = false
    this.render()
    this.updateDisplay()
  }

  // Mantiene coherentes los dos extremos en el camino sin calendario: el
  // "hasta" no puede ser anterior al "desde". Se resuelve con `min`/`max`
  // nativos —el navegador ya sabe rechazar la fecha fuera de rango— en vez de
  // validar a mano. El servidor ya los emite puestos.
  sync() {
    this.toTarget.min = this.fromTarget.value
    this.fromTarget.max = this.toTarget.value
  }

  toggle() {
    this.popTarget.hidden ? this.open() : this.close()
  }

  open() {
    this.from = parseISO(this.fromTarget.value)
    this.to = parseISO(this.toTarget.value)
    this.focusDate = this.from || this.to || today()
    this.viewMonth = startOfMonth(this.focusDate)

    this.popTarget.hidden = false
    this.triggerTarget.setAttribute("aria-expanded", "true")
    this.render()
    this.focusCurrentDay()
  }

  close() {
    if (this.popTarget.hidden) return

    this.popTarget.hidden = true
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this.triggerTarget.focus()
  }

  closeOnOutsideClick(event) {
    if (this.popTarget.hidden) return
    // Un día recién repintado: elegir el inicio vuelve a dibujar la grilla, así
    // que para cuando el click llega a window su botón ya no está en el
    // documento y `contains` lo daría por "fuera", cerrando el calendario
    // justo cuando falta elegir el término.
    if (!event.target.isConnected) return
    if (this.element.contains(event.target)) return

    this.popTarget.hidden = true
    this.triggerTarget.setAttribute("aria-expanded", "false")
  }

  prevMonth() {
    this.viewMonth = shiftMonth(this.viewMonth, -1)
    this.render()
  }

  nextMonth() {
    this.viewMonth = shiftMonth(this.viewMonth, 1)
    this.render()
  }

  // Un click por extremo. Sin rango en curso, o con uno ya cerrado, o con una
  // fecha anterior al inicio: empieza de nuevo desde ahí. Cerrar el rango es
  // el final del gesto, así que el popover se va solo.
  pick(event) {
    const date = parseISO(event.currentTarget.dataset.date)
    if (!date) return

    if (!this.from || this.to || date < this.from) {
      this.from = date
      this.to = null
    } else {
      this.to = date
    }

    this.focusDate = date
    this.commit()
    this.render()

    if (this.to) {
      this.close()
    } else {
      this.focusCurrentDay()
    }
  }

  clear() {
    this.from = null
    this.to = null
    this.commit()
    this.render()
    this.focusCurrentDay()
  }

  // Teclado dentro de la grilla: flechas de a día, PageUp/Down de a mes,
  // Home/End a los bordes de la semana. Enter y Espacio no se manejan acá —
  // cada día es un <button>, y activarlo ya dispara `pick`.
  navigate(event) {
    const steps = {
      ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7,
      Home: -this.focusDate.getDay(), End: 6 - this.focusDate.getDay()
    }

    let next = null
    if (event.key in steps) next = shiftDay(this.focusDate, steps[event.key])
    if (event.key === "PageUp") next = shiftMonth(this.focusDate, -1)
    if (event.key === "PageDown") next = shiftMonth(this.focusDate, 1)
    if (!next) return

    event.preventDefault()
    this.focusDate = next
    if (!this.isVisible(next)) this.viewMonth = startOfMonth(next)
    this.render()
    this.focusCurrentDay()
  }

  // Los inputs nativos siguen siendo lo que viaja: acá se les escribe y se les
  // avisa (`change`) para que cualquier filtro que se auto-envíe lo note.
  commit() {
    this.fromTarget.value = toISO(this.from)
    this.toTarget.value = toISO(this.to)
    this.sync()
    this.updateDisplay()

    for (const input of [this.fromTarget, this.toTarget]) {
      input.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  updateDisplay() {
    if (this.hasDisplayTarget) this.displayTarget.textContent = this.rangeLabel()
  }

  rangeLabel() {
    if (this.from && this.to) return `${this.format(this.from)} → ${this.format(this.to)}`
    if (this.from) return `${this.fromPrefixValue} ${this.format(this.from)}`
    if (this.to) return `${this.toPrefixValue} ${this.format(this.to)}`

    return this.placeholderValue
  }

  // Mismo formato que pinta el servidor en la primera carga ("1 sep 2026").
  format(date) {
    return `${date.getDate()} ${this.abbrMonthsValue[date.getMonth()]} ${date.getFullYear()}`
  }

  render() {
    const second = shiftMonth(this.viewMonth, 1)
    this.titleTarget.textContent = `${this.monthLabel(this.viewMonth)} — ${this.monthLabel(second)}`
    this.monthsTarget.replaceChildren(this.monthTable(this.viewMonth), this.monthTable(second))
    this.hintTarget.textContent = this.from && !this.to ? this.pickEndValue : this.pickStartValue
  }

  monthLabel(date) {
    return `${this.monthsValue[date.getMonth()]} ${date.getFullYear()}`
  }

  monthTable(month) {
    const table = document.createElement("table")
    table.className = "tp-datepicker__month"
    table.setAttribute("role", "grid")
    table.setAttribute("aria-label", this.monthLabel(month))

    const head = table.createTHead().insertRow()
    for (const day of this.weekdaysValue) {
      const cell = document.createElement("th")
      cell.scope = "col"
      cell.textContent = day
      head.append(cell)
    }

    const body = table.createTBody()
    for (const week of weeksOf(month)) {
      const row = body.insertRow()
      for (const date of week) row.append(this.dayCell(date, month))
    }

    return table
  }

  // Los días de meses vecinos quedan como celda vacía: pintarlos invita a
  // elegir en la grilla equivocada, y el mes de al lado ya está a la vista.
  dayCell(date, month) {
    const cell = document.createElement("td")
    if (!date || date.getMonth() !== month.getMonth()) return cell

    const button = document.createElement("button")
    button.type = "button"
    button.className = "tp-datepicker__day"
    button.textContent = String(date.getDate())
    button.dataset.date = toISO(date)
    button.dataset.action = "v2--date-range#pick"
    button.tabIndex = sameDay(date, this.focusDate) ? 0 : -1

    if (sameDay(date, today())) button.classList.add("is-today")
    if (sameDay(date, this.from) || sameDay(date, this.to)) button.classList.add("is-edge")
    if (this.inRange(date)) button.classList.add("is-in-range")
    button.setAttribute("aria-pressed", String(button.classList.contains("is-edge")))

    cell.append(button)
    return cell
  }

  inRange(date) {
    return Boolean(this.from && this.to && date >= this.from && date <= this.to)
  }

  isVisible(date) {
    const first = this.viewMonth
    const last = shiftMonth(this.viewMonth, 2)
    return date >= first && date < last
  }

  focusCurrentDay() {
    this.monthsTarget.querySelector(`[data-date="${toISO(this.focusDate)}"]`)?.focus()
  }
}

// --- Fechas: locales y sin hora. `new Date(iso)` parsea UTC y en Chile
// devuelve el día anterior, así que todo se arma con el constructor de partes.

function parseISO(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "")
  if (!match) return null

  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return Number.isNaN(date.getTime()) ? null : date
}

function toISO(date) {
  if (!date) return ""

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0")
  ].join("-")
}

function today() {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function shiftMonth(date, months) {
  return new Date(date.getFullYear(), date.getMonth() + months, date.getDate())
}

function shiftDay(date, days) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)
}

function sameDay(a, b) {
  return Boolean(a && b && a.getTime() === b.getTime())
}

// Semanas de lunes a domingo que cubren el mes, con `null` en los huecos de
// los bordes (los días del mes vecino no se dibujan).
function weeksOf(month) {
  const first = startOfMonth(month)
  const lead = (first.getDay() + 6) % 7
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
  const cells = Array(lead).fill(null)

  for (let day = 1; day <= days; day++) {
    cells.push(new Date(month.getFullYear(), month.getMonth(), day))
  }
  while (cells.length % 7 !== 0) cells.push(null)

  const weeks = []
  for (let index = 0; index < cells.length; index += 7) weeks.push(cells.slice(index, index + 7))

  return weeks
}
